// Order one department's org-chart positions manager-first and resolve the
// supervisor name to show on each card.
//
// Source of truth is staff.supervisor_id (surfaced by GET /api/org-chart as
// supervisor_first/last). When a filled position has no supervisor set, we fall
// back to the department's manager FOR DISPLAY ONLY — we never change the data,
// so time-off approval routing is unaffected.
//
// Ordering per department:
//   0) filled leads   (a designated supervisor, or reports to someone outside the dept)
//   1) filled reports (people whose supervisor sits inside the dept / inferred)
//   2) vacant positions
// Within reports, people with a real supervisor set come before inferred ones,
// then alphabetical — this yields e.g. Kristin, Kaitlyn, Denise for Collections.

const nameOf = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();
const explicitSupervisor = (p) =>
  (p.supervisor_first || p.supervisor_last)
    ? `${p.supervisor_first || ''} ${p.supervisor_last || ''}`.trim()
    : null;
// Vacant/incoming cards (openings) carry an intended supervisor instead of a staff one —
// use it so they nest under their lead like filled reports do.
const intendedSupervisor = (p) =>
  (p.intended_supervisor_first || p.intended_supervisor_last)
    ? `${p.intended_supervisor_first || ''} ${p.intended_supervisor_last || ''}`.trim()
    : null;

export function layoutDepartment(deptPositions) {
  const deptStaffIds = new Set(deptPositions.filter(p => p.staff_id).map(p => p.staff_id));

  const isLead = (p) =>
    p.status === 'filled' &&
    (p.person_is_supervisor || (p.person_supervisor_id && !deptStaffIds.has(p.person_supervisor_id)));

  // Department manager for the display-only fallback: the designated supervisor
  // in the dept, else the first lead.
  const manager =
    deptPositions.find(p => p.status === 'filled' && p.person_is_supervisor) ||
    deptPositions.find(isLead) ||
    null;
  const managerName = manager ? nameOf(manager) : null;
  const managerStaffId = manager ? manager.staff_id : null;

  const enriched = deptPositions.map(p => {
    let supervisorName = explicitSupervisor(p);
    const explicit = !!supervisorName;
    // Vacant/incoming openings nest under their intended supervisor…
    if (!supervisorName && p.status !== 'filled') {
      supervisorName = intendedSupervisor(p);
    }
    // …filled people with no supervisor set fall back to the dept manager (display only).
    if (!supervisorName && p.status === 'filled' && managerName && p.staff_id !== managerStaffId) {
      supervisorName = managerName;
    }
    return { pos: p, supervisorName, _explicit: explicit, isLead: isLead(p) };
  });

  const groupKey = (e) => (e.pos.status === 'vacant' ? 2 : isLead(e.pos) ? 0 : 1);
  enriched.sort((a, b) => {
    const ga = groupKey(a), gb = groupKey(b);
    if (ga !== gb) return ga - gb;
    if (a._explicit !== b._explicit) return a._explicit ? -1 : 1;
    return nameOf(a.pos).localeCompare(nameOf(b.pos));
  });

  return enriched; // [{ pos, supervisorName }]
}

// Group a department into side-by-side sub-columns: each lead (sub-department head)
// with the reports that sit under it (matched by the supervisor name we resolved),
// so reports render directly beneath their head with a connecting line. Reports that
// don't match any lead (and head-less vacancies) collect in a trailing column.
export function layoutDepartmentColumns(deptPositions) {
  const items = layoutDepartment(deptPositions);
  const leads = items.filter(e => e.isLead);
  const others = items.filter(e => !e.isLead);
  const cols = leads.map(lead => ({ lead, reports: [] }));
  const byName = new Map(cols.map(c => [nameOf(c.lead.pos), c]));
  const leftovers = [];
  others.forEach(o => {
    const col = o.supervisorName ? byName.get(o.supervisorName) : null;
    if (col) col.reports.push(o); else leftovers.push(o);
  });
  if (leftovers.length) cols.push({ lead: null, reports: leftovers });
  return cols;
}

// Supervisor name for a single position (no dept context / fallback) — used for
// the Executive cards.
export function supervisorName(p) {
  return explicitSupervisor(p);
}
